/**
 * CountryMatrix (C-1e) — the registration portfolio as a market × lifecycle
 * cross-tab. One row per (market, authority) with counts by stage bucket so RA
 * can see where each market sits at a glance. Route: /regulatory/market-matrix
 */
import { PageHero } from '@edms/design-system';
import styles from './Regulatory.module.css';
import { useMarketMatrixQuery } from './useRegulatory';

function pct(part: number, whole: number) {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

export function CountryMatrix() {
  const q = useMarketMatrixQuery();
  const markets = q.data?.markets ?? [];
  const totals = q.data?.totals;

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Regulatory Affairs · RA-PLM"
        title="Country / Market Matrix"
        subtitle="The registration portfolio by market and lifecycle stage — live vs in-progress vs withdrawn, per authority."
      />

      {totals && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{q.data?.marketCount ?? 0}</span><span className={styles.kpiLabel}>Markets</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{totals.launched + totals.approved}</span><span className={styles.kpiLabel}>Live (approved/launched)</span></div>
          <div className={`${styles.kpi} ${totals.inProgress > 0 ? styles.kpiWarn : ''}`}><span className={styles.kpiNum}>{totals.inProgress}</span><span className={styles.kpiLabel}>In progress</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{totals.total}</span><span className={styles.kpiLabel}>Total registrations</span></div>
        </div>
      )}

      {q.isLoading ? (
        <div className={styles.empty}>Loading market matrix…</div>
      ) : markets.length === 0 ? (
        <div className={styles.empty}>No registrations yet — the matrix populates as registrations are tracked per market.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Market</th><th>Authority</th><th>Live coverage</th>
              <th>Launched</th><th>Approved</th><th>In progress</th><th>Withdrawn</th><th>Total</th>
            </tr>
          </thead>
          <tbody>
            {markets.map((m) => {
              const live = m.launched + m.approved;
              return (
                <tr key={`${m.market}|${m.authority}`}>
                  <td><strong>{m.market}</strong></td>
                  <td>{m.authority}</td>
                  <td>
                    <div className={styles.breakBarWrap} title={`${live}/${m.total} live`}>
                      <div className={styles.breakBar} style={{ width: `${pct(live, m.total)}%` }} />
                    </div>
                    <span className={styles.sub}>{pct(live, m.total)}%</span>
                  </td>
                  <td>{m.launched}</td>
                  <td>{m.approved}</td>
                  <td>{m.inProgress}</td>
                  <td>{m.withdrawn}</td>
                  <td className={styles.mono}>{m.total}</td>
                </tr>
              );
            })}
            {totals && (
              <tr className={styles.terminal}>
                <td><strong>All markets</strong></td>
                <td>—</td>
                <td>{pct(totals.launched + totals.approved, totals.total)}%</td>
                <td>{totals.launched}</td>
                <td>{totals.approved}</td>
                <td>{totals.inProgress}</td>
                <td>{totals.withdrawn}</td>
                <td className={styles.mono}>{totals.total}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </main>
  );
}
